import {
  ComponentPropsWithoutRef,
  FC,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Delete } from '@mui/icons-material';
import Stack from '@mui/material/Stack';

import MetadataField from '@/shared/components/forms/MetadataField/MetadataField';
import MultiSelectMetadata from '@/shared/components/forms/MetadataField/components/MultiSelectMetadata';
import WaveUIAccordion from '@/shared/components/widgets/WaveUIAccordion';
import Metadata from '@/shared/types/metadata/index.type';

import transformMetadata from '../utils/transformeMetadata';

type MetadataFormAccordionProps = {
  metadata: Metadata;
  levelConstraint: 'PRODUCT' | 'VARIANT' | 'INVENTORY' | 'ATOM';
  fields: {
    required: Record<string, unknown>;
    optional: Record<string, unknown>;
  };
  isRemoveIconDisabled?: boolean;
  accordionProps: Omit<
    ComponentPropsWithoutRef<typeof WaveUIAccordion>,
    'children'
  >;
  initialData?: Record<string, unknown>;
  getValues: (
    fields: Partial<{
      required: Record<string, unknown>;
      optional: Record<string, unknown>;
    }>,
  ) => void;
  onRemoveIconClick?: () => void;
};

const MetadataFormAccordion: FC<MetadataFormAccordionProps> = ({
  metadata,
  levelConstraint,
  fields,
  isRemoveIconDisabled,
  accordionProps,
  initialData,
  getValues,
  onRemoveIconClick,
}) => {
  const initialDataRef = useRef<
    MetadataFormAccordionProps['initialData'] | null
  >(null);

  const [productOptionalSelectedMetadata, setProductOptionalSelectedMetadata] =
    useState<string[]>([]);

  const optionalMetadata = transformMetadata({
    metadataDefinitions: metadata.metadata_definitions,
    requiredLevel: 'OPTIONAL',
    levelConstraint: levelConstraint,
    initialData: initialData,
  });

  const requiredMetadata = transformMetadata({
    metadataDefinitions: metadata.metadata_definitions,
    requiredLevel: 'REQUIRED',
    levelConstraint: levelConstraint,
    initialData: initialData,
  });

  useEffect(() => {
    if (initialData !== null && initialDataRef.current === null) {
      initialDataRef.current = initialData;

      const optionalMetadataKeys = optionalMetadata.map(
        (item) => item.metadata_key,
      );

      const selectedItems: string[] = [];

      for (const key in initialData) {
        if (
          optionalMetadataKeys.includes(key) &&
          initialData[key] !== null &&
          initialData[key] !== undefined
        ) {
          selectedItems.push(key);
        }
      }

      setProductOptionalSelectedMetadata(selectedItems);
    }
  }, [
    initialData,
    levelConstraint,
    metadata.metadata_definitions,
    optionalMetadata,
    productOptionalSelectedMetadata,
  ]);

  return (
    <WaveUIAccordion
      {...accordionProps}
      defaultExpanded
      actions={
        onRemoveIconClick
          ? [
              {
                label: '',
                disabled: isRemoveIconDisabled,
                icon: <Delete sx={{ width: 20, height: 20 }} />,
                onClick: onRemoveIconClick,
              },
            ]
          : undefined
      }
    >
      <Stack spacing={2}>
        {requiredMetadata.map((item) => {
          return (
            <MetadataField
              key={item.metadata_key}
              metadataDefinition={item}
              getFieldValue={(fieldValue) =>
                getValues({ required: fieldValue })
              }
            />
          );
        })}
      </Stack>

      <WaveUIAccordion title='Details' defaultExpanded>
        <MultiSelectMetadata
          optionalMetadata={optionalMetadata}
          selectedMetadata={productOptionalSelectedMetadata}
          onSelectMetadata={(selectedKeys: string[]) => {
            setProductOptionalSelectedMetadata(selectedKeys);

            const filteredProducts = Object.keys(fields.optional)
              .filter((key) => selectedKeys.includes(key))
              .reduce(
                (acc, key) => {
                  acc[key] = fields.optional[key];
                  return acc;
                },
                {} as Record<string, unknown>,
              );

            getValues({ optional: filteredProducts });
          }}
        />

        {optionalMetadata
          .filter((item) =>
            productOptionalSelectedMetadata.includes(item.metadata_key),
          )
          .map((item) => {
            return (
              <MetadataField
                key={item.metadata_key}
                metadataDefinition={item}
                getFieldValue={(fieldValue) =>
                  getValues({ optional: fieldValue })
                }
              />
            );
          })}
      </WaveUIAccordion>
    </WaveUIAccordion>
  );
};

export default MetadataFormAccordion;
